import { useState } from 'react';
import { useQuery } from '@apollo/client';
import {
  MEU_RESUMO_FINANCEIRO,
  MINHA_SERIE_POR_DIA,
  MeuResumoFinanceiroData,
  MinhaSeriePorDiaData,
  FinanceiroFiltroVars,
} from '../services/financeiroService';
import type { ResumoFinanceiro, PontoSerieFinanceira, PeriodoFinanceiro } from '../types/financeiro';

// Ponto único de estado do hub. Regras do corpo (Task 6 do plano):
// 1. useQuery, não o service imperativo — é o que MockedProvider intercepta (ver Notes do
//    plano), e ai/architecture.core.md §5 autoriza hooks a buscar dados. O gql continua vindo
//    de services/, então "GraphQL só em services" é respeitado.
// 2. O null do contrato nullable da F-1 é um estado tratável, não um crash — resumoIndisponivel
//    é distinto de error (falha de rede) e de vazio (período sem lançamentos).
// 3. refetch exposto para a ação "Tentar novamente" de AC-12.
// 4. Nenhum useMemo/useCallback sem justificativa (ai/anti-patterns.md §7).
// 5. Nenhuma soma de valor monetário — o hook repassa o que veio da query, sem reduce.

export interface UseFinanceiroAtletaResult {
  periodo: PeriodoFinanceiro;
  setPeriodo: (periodo: PeriodoFinanceiro) => void;
  resumo: ResumoFinanceiro | null;
  serie: PontoSerieFinanceira[];
  loading: boolean;
  error: Error | undefined;
  resumoIndisponivel: boolean;
  refetch: () => void;
}

function pad(n: number): string {
  return String(n).padStart(2, '0');
}

function formatDateLocal(date: Date): string {
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Primeiro e último dia do mês corrente, por aritmética de Date LOCAL (nunca UTC) — evita a
// classe de bug de fuso que o B-6 fechou no backend.
function periodoMesCorrente(): PeriodoFinanceiro {
  const agora = new Date();
  const inicio = new Date(agora.getFullYear(), agora.getMonth(), 1);
  const fim = new Date(agora.getFullYear(), agora.getMonth() + 1, 0);
  return { inicio: formatDateLocal(inicio), fim: formatDateLocal(fim) };
}

export function useFinanceiroAtleta(): UseFinanceiroAtletaResult {
  const [periodo, setPeriodo] = useState<PeriodoFinanceiro>(periodoMesCorrente());

  const resumoQuery = useQuery<MeuResumoFinanceiroData, FinanceiroFiltroVars>(MEU_RESUMO_FINANCEIRO, {
    variables: { filtro: periodo },
    fetchPolicy: 'no-cache',
  });

  const serieQuery = useQuery<MinhaSeriePorDiaData, FinanceiroFiltroVars>(MINHA_SERIE_POR_DIA, {
    variables: { filtro: periodo },
    fetchPolicy: 'no-cache',
  });

  const resumo = resumoQuery.data?.meuResumoFinanceiro ?? null;
  const serie = serieQuery.data?.minhaSeriePorDia ?? [];
  const loading = resumoQuery.loading || serieQuery.loading;
  const error = resumoQuery.error || serieQuery.error;
  const resumoIndisponivel = !resumoQuery.loading && !resumoQuery.error && resumo === null;

  const refetch = () => {
    resumoQuery.refetch();
    serieQuery.refetch();
  };

  return { periodo, setPeriodo, resumo, serie, loading, error, resumoIndisponivel, refetch };
}
